/**
 * Pure state transitions for requests. Each function mutates the request it
 * is given (the store passes in a clone), so it can run inside `store.update`.
 */
import { getPerson } from '../../data/company';
import type { PersonId } from '../../data/types';
import {
  approverName,
  currentApprovalStep,
  isRouted,
  STAGE_LABEL,
} from './requests-helpers';
import {
  REQUEST_STAGES,
  type ActivityActor,
  type ProcurementRequest,
  type RequestStage,
} from './requests-types';
import { buildDirectRequests } from './seed-direct';
import { buildIndirectRequests } from './seed-indirect';

/** Seed list, newest request id first. */
export function buildSeedRequests(): ProcurementRequest[] {
  return [...buildDirectRequests(), ...buildIndirectRequests()].sort((a, b) =>
    b.id.localeCompare(a.id),
  );
}

function pushActivity(
  req: ProcurementRequest,
  actor: ActivityActor,
  text: string,
  at: number,
  detail?: string,
): void {
  const entry = {
    id: `${req.id}-act-${req.activity.length + 1}-${at}`,
    at,
    actor,
    text,
  };
  req.activity.push(detail ? { ...entry, detail } : entry);
}

const AFTER_APPROVAL_NOTE: Record<RequestStage, string> = {
  intake: 'Back with the requester',
  review: 'Waiting on approvals',
  sourcing: 'Approved, sourcing under way',
  contracting: 'Approved, contract in progress',
  ordering: 'Approved, purchase order drafted',
  done: 'Approved and closed',
};

function moveToStage(req: ProcurementRequest, target: RequestStage): void {
  const from = REQUEST_STAGES.indexOf(req.stage);
  const to = REQUEST_STAGES.indexOf(target);
  for (let i = from + 1; i < to; i += 1) {
    const skipped = REQUEST_STAGES[i];
    if (skipped && !req.skippedStages.includes(skipped)) {
      req.skippedStages.push(skipped);
    }
  }
  req.skippedStages = req.skippedStages.filter((s) => s !== target);
  req.stage = target;
  req.stageNote = AFTER_APPROVAL_NOTE[target];
}

/** Approve the current open step. When it was the last one, advance the request. */
export function approveCurrentStep(req: ProcurementRequest, now: number): void {
  const step = currentApprovalStep(req);
  if (!step || step.status !== 'open' || !isRouted(req)) return;

  step.status = 'approved';
  step.decidedAt = now;
  pushActivity(
    req,
    step.approver,
    `${step.role} approval: ${approverName(step.approver)} approved`,
    now,
  );

  const next = currentApprovalStep(req);
  if (next) {
    req.stageNote = `Waiting on ${next.role.toLowerCase()}`;
    pushActivity(
      req,
      { kind: 'agent' },
      `Routed to ${approverName(next.approver)} for ${next.role.toLowerCase()} approval`,
      now + 1,
    );
    return;
  }

  const target =
    req.onApproved ?? (req.stage === 'review' ? 'ordering' : req.stage);
  moveToStage(req, target);
  pushActivity(
    req,
    { kind: 'agent' },
    req.onApprovedNote ??
      `All approvals complete. Moved to ${STAGE_LABEL[target].toLowerCase()}.`,
    now + 1,
  );
}

/** Send the current step back to the requester with a note. */
export function requestChangesOnCurrentStep(
  req: ProcurementRequest,
  note: string,
  now: number,
): void {
  const step = currentApprovalStep(req);
  if (!step || step.status !== 'open' || !isRouted(req)) return;

  step.status = 'changes_requested';
  step.decidedAt = now;
  step.note = note;
  req.stageNote = `Changes requested by ${approverName(step.approver)}`;
  pushActivity(
    req,
    step.approver,
    `${step.role}: ${approverName(step.approver)} requested changes`,
    now,
    note,
  );
  pushActivity(
    req,
    { kind: 'agent' },
    'Sent the request back to the requester with the approver note',
    now + 1,
  );
}

/** Reopen a step after the requester has updated the request. */
export function reopenCurrentStep(req: ProcurementRequest, now: number): void {
  const step = currentApprovalStep(req);
  if (!step || step.status !== 'changes_requested') return;

  step.status = 'open';
  delete step.decidedAt;
  delete step.note;
  req.stageNote = `Waiting on ${step.role.toLowerCase()}`;
  pushActivity(
    req,
    { kind: 'agent' },
    `Requester updated the request. Approval reopened for ${approverName(step.approver)}`,
    now,
  );
}

/** Add a person as a watcher. Does nothing when they already follow the request. */
export function addWatcher(
  req: ProcurementRequest,
  personId: PersonId,
  now: number,
): void {
  const watchers = req.watchers ?? [];
  if (watchers.includes(personId) || req.requesterId === personId) return;

  req.watchers = [...watchers, personId];
  pushActivity(
    req,
    { kind: 'person', id: personId },
    `${getPerson(personId).name} joined as a watcher`,
    now,
    'Joined from intake instead of opening a duplicate request.',
  );
}
